import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import popupImage from '../assets/images/popupImage.jpg';
import './QuoteDialog.css';

const QuoteDialog = ({ isOpen, onClose, title = "Get a Quote" }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // EmailJS configuration
  const SERVICE_ID = 'service_h7x96dl';
  const TEMPLATE_ID = 'template_fbtuqz1';
  const PUBLIC_KEY = '7fmn-jafokOBcnJhv';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEmailBlur = (e) => {
    const { value } = e.target;
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address'
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send email using EmailJS
      const result = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: formData.fullName,
          from_email: formData.email,
          to_email: 'industradejodhpur@gmail.com',
          message: `New quote request from ${formData.fullName} (${formData.email})`
        },
        PUBLIC_KEY
      );

      console.log('Email sent successfully:', result);
      setSubmitStatus('success');
      setFormData({ fullName: '', email: '' });
      setErrors({});
      
      // Close dialog after 2 seconds on success
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 2000);

    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ fullName: '', email: '' });
    setErrors({});
    setSubmitStatus(null);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Prevent body scroll when dialog is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="quote-dialog-overlay" onClick={handleOverlayClick}>
      <div className="quote-dialog-container">
        {/* Close button */}
        <button 
          className="quote-dialog-close-btn"
          onClick={handleClose}
          aria-label="Close dialog"
        >
          ×
        </button>
        
        <div className="quote-dialog-content">
          {/* Left side - Form */}
          <div className="quote-dialog-form-section">
            <h2 className="quote-dialog-title">Get Your Customized Quote with Indus Trade</h2>
            
            <form className="quote-dialog-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`quote-input ${errors.fullName ? 'error' : ''}`}
                  placeholder="FULL NAME"
                  disabled={isSubmitting}
                />
                {errors.fullName && (
                  <span className="error-message">{errors.fullName}</span>
                )}
              </div>

              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleEmailBlur}
                  className={`quote-input ${errors.email ? 'error' : ''}`}
                  placeholder="EMAIL ADDRESS"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="status-message success-message">
                  ✓ Thank you! Your quote request has been sent successfully.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="status-message error-message">
                  ✗ Sorry, there was an error sending your request. Please try again.
                </div>
              )}

              <button 
                type="submit" 
                className="quote-dialog-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="button-content">
                    <span className="loading-spinner"></span>
                    SENDING...
                  </span>
                ) : (
                  'SUBMIT'
                )}
              </button>
            </form>
          </div>

          {/* Right side - Image */}
          <div className="quote-dialog-image-section">
            <img src={popupImage} alt="Indus Trade Furniture" className="quote-dialog-image" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDialog;
